/**
 * Constantes e utilitários para ícones de arquivos no chat.
 * Extraído de chat.ts para reduzir o tamanho da classe AiChat.
 */

export const languageMap: Record<string, string> = {
    'js': 'JavaScript',
    'javascript': 'JavaScript',
    'ts': 'TypeScript',
    'typescript': 'TypeScript',
    'php': 'PHP',
    'py': 'Python',
    'python': 'Python',
    'rb': 'Ruby',
    'ruby': 'Ruby',
    'java': 'Java',
    'cs': 'C#',
    'csharp': 'C#',
    'cpp': 'C++',
    'c': 'C',
    'go': 'Go',
    'rust': 'Rust',
    'rs': 'Rust',
    'swift': 'Swift',
    'kotlin': 'Kotlin',
    'kt': 'Kotlin',
    'sql': 'SQL',
    'html': 'HTML',
    'css': 'CSS',
    'scss': 'SCSS',
    'sass': 'Sass',
    'less': 'Less',
    'json': 'JSON',
    'xml': 'XML',
    'yaml': 'YAML',
    'yml': 'YAML',
    'md': 'Markdown',
    'markdown': 'Markdown',
    'bash': 'Terminal',
    'sh': 'Terminal',
    'shell': 'Terminal',
    'zsh': 'Terminal',
    'terminal': 'Terminal',
    'console': 'Terminal',
    'cmd': 'CMD',
    'powershell': 'PowerShell',
    'ps1': 'PowerShell',
    'dockerfile': 'Dockerfile',
    'docker': 'Docker',
    'nginx': 'Nginx',
    'apache': 'Apache',
    'env': 'Environment',
    'ini': 'INI',
    'toml': 'TOML',
    'graphql': 'GraphQL',
    'vue': 'Vue',
    'jsx': 'JSX',
    'tsx': 'TSX',
    'blade': 'Blade',
    'twig': 'Twig',
};

export const terminalLanguages: string[] = [
    'bash', 'sh', 'shell', 'zsh', 'terminal', 'console', 'cmd', 'powershell', 'ps1',
];

export function getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();

    const config: { icon: string; color: string } = ((): { icon: string; color: string } => {
        switch (extension) {
            case 'php': return { icon: 'code', color: '#777BB4' };
            case 'java': case 'jar': return { icon: 'code', color: '#007396' };
            case 'py': case 'python': return { icon: 'code', color: '#3776AB' };
            case 'go': return { icon: 'code', color: '#00ADD8' };
            case 'rb': case 'ruby': return { icon: 'code', color: '#CC342D' };
            case 'c': case 'cpp': case 'h': return { icon: 'code', color: '#5E97D0' };
            case 'cs': return { icon: 'code', color: '#239120' };

            case 'js': case 'jsx': return { icon: 'code-2', color: '#F7DF1E' };
            case 'ts': case 'tsx': return { icon: 'code-2', color: '#3178C6' };
            case 'html': case 'htm': return { icon: 'code', color: '#E34F26' };
            case 'css': return { icon: 'code', color: '#1572B6' };
            case 'scss': case 'sass': return { icon: 'code', color: '#CC6699' };
            case 'vue': return { icon: 'code', color: '#4FC08D' };
            case 'blade.php': return { icon: 'code', color: '#FF2D20' };

            case 'json': return { icon: 'json', color: '#000000' };
            case 'xml': return { icon: 'code', color: '#0060AC' };
            case 'yaml': case 'yml': return { icon: 'file-text', color: '#CB171E' };
            case 'sql': return { icon: 'database', color: '#4479A1' };
            case 'env': return { icon: 'cog', color: '#333333' };

            case 'pdf': return { icon: 'file-text', color: '#F40F02' };
            case 'doc': case 'docx': return { icon: 'file-text', color: '#2B579A' };
            case 'xls': case 'xlsx': case 'csv': return { icon: 'bar-chart', color: '#217346' };
            case 'ppt': case 'pptx': return { icon: 'presentation', color: '#D24726' };
            case 'zip': case 'rar': case '7z': case 'tar': case 'gz': return { icon: 'archive', color: '#FDBE00' };
            case 'jpg': case 'jpeg': case 'png': case 'gif': case 'webp': case 'svg': return { icon: 'image', color: '#222222' };
            case 'mp3': case 'wav': case 'ogg': return { icon: 'music', color: '#999999' };
            case 'mp4': case 'avi': case 'mov': return { icon: 'video', color: '#999999' };
            case 'txt': case 'md': return { icon: 'file-text', color: '#555555' };
            case 'drawio': case 'vsdx': return { icon: 'diagram', color: '#007bff' };

            default: return { icon: 'file', color: '#6B7280' };
        }
    })();

    const { icon, color } = config;

    let paths = '';

    paths += '<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>';
    paths += '<polyline points="14 2 14 8 20 8"/>';

    switch (icon) {
        case 'code':
            paths += '<polyline points="16 13 20 17 16 21"/>';
            paths += '<polyline points="8 21 4 17 8 13"/>';
            break;
        case 'code-2':
            paths += '<path d="m10 13-2 2 2 2"/>';
            paths += '<path d="m14 17 2-2-2-2"/>';
            break;
        case 'json':
            paths += '<path d="M10 13a1 1 0 0 0-1 1v1a1 1 0 0 1-1 1 1 1 0 0 1 1 1v1a1 1 0 0 0 1 1"/>';
            paths += '<path d="M14 20a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1 1 1 0 0 1-1-1v-1a1 1 0 0 0-1-1"/>';
            break;
        case 'image':
            paths += '<circle cx="10" cy="13" r="2"/>';
            paths += '<path d="m20 17-1.09-1.09a2 2 0 0 0-2.82 0L10 22"/>';
            break;
        case 'file-text':
            paths += '<line x1="16" x2="8" y1="13" y2="13"/>';
            paths += '<line x1="16" x2="8" y1="17" y2="17"/>';
            paths += '<line x1="10" x2="8" y1="9" y2="9"/>';
            break;
        case 'archive':
            paths = '';
            paths += '<path d="M4 8V4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4"/>';
            paths += '<path d="M4 8h16v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8Z"/>';
            paths += '<path d="M10 8v4"/>';
            paths += '<path d="M14 8v4"/>';
            break;
        case 'database':
            paths += '<ellipse cx="12" cy="5" rx="9" ry="3"/>';
            paths += '<path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>';
            paths += '<path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>';
            break;
        case 'bar-chart':
            paths += '<path d="M12 18v-6"/>';
            paths += '<path d="M8 18v-1"/>';
            paths += '<path d="M16 18v-3"/>';
            break;
        case 'cog':
            paths += '<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.47a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>';
            paths += '<circle cx="12" cy="12" r="3"/>';
            break;
        case 'presentation':
            paths += '<path d="M2 3h20"/>';
            paths += '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>';
            paths += '<path d="M7 3v12h10V3"/>';
            paths += '<path d="m12 7 5 4H7l5-4z"/>';
            break;
        case 'music':
            paths += '<path d="M9 18V5l12-2v13"/>';
            paths += '<circle cx="6" cy="18" r="3"/>';
            paths += '<circle cx="18" cy="15" r="3"/>';
            break;
        case 'video':
            paths += '<path d="m22 8-6 4 6 4V8Z"/>';
            paths += '<rect x="2" y="8" width="14" height="8" rx="2" ry="2"/>';
            break;
        case 'diagram':
            paths += '<rect x="3" y="3" width="7" height="7" rx="1"/>';
            paths += '<rect x="14" y="3" width="7" height="7" rx="1"/>';
            paths += '<rect x="14" y="14" width="7" height="7" rx="1"/>';
            paths += '<rect x="3" y="14" width="7" height="7" rx="1"/>';
            paths += '<path d="M7 10v4"/>';
            paths += '<path d="M17 10v4"/>';
            paths += '<path d="M10 7h4"/>';
            paths += '<path d="M10 17h4"/>';
            break;
        case 'file':
        default:
            break;
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-${icon}">${paths}</svg>`;
}
